import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fetchCampaigns } from "../api/campaigns";
import {
    getMyDeliveryShareStatistics,
    getTodayDeliveriesStatistics,
    type UserDeliveryShareStatistics
} from "../api/deliveries";
import { electionDayDailyTarget } from "../shared/electionDayGoals";
import { WwwPortalNavBar } from "./WwwPortalNavBar";
import "./StatsPage.css";

const qtyFmt = (n: number, lng: string) =>
    new Intl.NumberFormat(lng === "en" || lng.startsWith("en") ? "en-GB" : "sv-SE").format(n);

function hasAnyGoal(s: UserDeliveryShareStatistics): boolean {
    return s.weekGoalQuantity != null || s.monthGoalQuantity != null || s.yearGoalQuantity != null;
}

function GoalRow({
    label,
    current,
    goal,
    variant,
    lng
}: {
    label: string;
    current: number;
    goal: number;
    variant: "week" | "month" | "year" | "day";
    lng: string;
}): JSX.Element {
    const safeGoal = Math.max(goal, 1);
    const pctRaw = (current / safeGoal) * 100;
    const pctBar = Math.min(100, pctRaw);
    const pctLabel = Math.min(999, Math.round(pctRaw));
    const complete = pctRaw >= 100;
    return (
        <div className={`stats-page__goal stats-page__goal--${variant}${complete ? " stats-page__goal--complete" : ""}`}>
            <div className="stats-page__goal-head">
                <span className="stats-page__goal-label">{label}</span>
                <span className="stats-page__goal-pct">{pctLabel}%</span>
            </div>
            <div className="stats-page__goal-track">
                <div className="stats-page__goal-fill" style={{ width: `${pctBar}%` }} />
            </div>
            <div className="stats-page__goal-nums">
                {qtyFmt(current, lng)} / {qtyFmt(goal, lng)}
            </div>
        </div>
    );
}

function WeekBars({
    days,
    lng,
    ariaLabel
}: {
    days: { date: string; totalQuantity: number }[];
    lng: string;
    ariaLabel: string;
}): JSX.Element {
    const maxQ = Math.max(1, ...days.map((d) => d.totalQuantity));
    const weekdayFmt = new Intl.DateTimeFormat(lng === "en" || lng.startsWith("en") ? "en-GB" : "sv-SE", {
        weekday: "short"
    });
    return (
        <div className="stats-page__week" role="group" aria-label={ariaLabel}>
            {days.map((d) => {
                const dateObj = new Date(d.date + "T12:00:00");
                const h = (d.totalQuantity / maxQ) * 100;
                const label = weekdayFmt.format(dateObj);
                return (
                    <div key={d.date} className="stats-page__week-day">
                        <div className="stats-page__week-col">
                            {d.totalQuantity > 0 && (
                                <span className="stats-page__week-qty">{qtyFmt(d.totalQuantity, lng)}</span>
                            )}
                            <div className="stats-page__week-bar-wrap">
                                <div className="stats-page__week-bar" style={{ height: `${h}%` }} />
                            </div>
                        </div>
                        <span className="stats-page__week-label">{label}</span>
                    </div>
                );
            })}
        </div>
    );
}

export const StatsPage = (): JSX.Element => {
    const { t, i18n } = useTranslation();
    const lng = i18n.language ?? "sv";
    const [searchParams] = useSearchParams();
    const campaignId = searchParams.get("campaignId") || undefined;

    const shareQuery = useQuery({
        queryKey: ["deliveries", "statistics", "me"],
        queryFn: getMyDeliveryShareStatistics,
        staleTime: 30 * 1000
    });

    const campaignsQuery = useQuery({
        queryKey: ["campaigns"],
        queryFn: fetchCampaigns,
        staleTime: 5 * 60 * 1000
    });

    const todayCampaignQuery = useQuery({
        queryKey: ["deliveries", "today", campaignId],
        queryFn: () => getTodayDeliveriesStatistics(campaignId),
        enabled: !!campaignId,
        staleTime: 10 * 1000
    });

    const campaignName = useMemo(() => {
        if (!campaignId || !campaignsQuery.data) return null;
        return campaignsQuery.data.find((c) => c.id === campaignId)?.name ?? null;
    }, [campaignId, campaignsQuery.data]);

    const updatedLabel = useMemo(() => {
        const d = new Date();
        return new Intl.DateTimeFormat(lng === "en" || lng.startsWith("en") ? "en-GB" : "sv-SE", {
            dateStyle: "medium",
            timeStyle: "short"
        }).format(d);
    }, [lng]);

    const electionDayPace = useMemo(() => {
        const data = shareQuery.data;
        if (!data || data.yearGoalQuantity == null) {
            return null;
        }
        return electionDayDailyTarget(data.yearGoalQuantity, data.calendarYearTotalQuantity, new Date());
    }, [shareQuery.data]);

    if (shareQuery.isLoading) {
        return (
            <div className="stats-page">
                <div className="stats-page__topbar">
                    <header className="stats-page__header">
                        <h1 className="stats-page__title">{t("stats.title")}</h1>
                    </header>
                    <div className="stats-page__topbar-actions">
                        <WwwPortalNavBar surface="www_stats" />
                    </div>
                </div>
                <div className="stats-page__content">
                    <div className="stats-page__loading">{t("stats.loading")}</div>
                </div>
                <footer className="stats-page__footer">
                    <Link to="/" className="stats-page__back">
                        ← {t("stats.backToMap")}
                    </Link>
                </footer>
            </div>
        );
    }

    if (shareQuery.isError || !shareQuery.data) {
        return (
            <div className="stats-page">
                <div className="stats-page__topbar">
                    <header className="stats-page__header">
                        <h1 className="stats-page__title">{t("stats.title")}</h1>
                    </header>
                    <div className="stats-page__topbar-actions">
                        <WwwPortalNavBar surface="www_stats" />
                    </div>
                </div>
                <div className="stats-page__content">
                    <p className="stats-page__error">{t("stats.error")}</p>
                </div>
                <footer className="stats-page__footer">
                    <Link to="/" className="stats-page__back">
                        ← {t("stats.backToMap")}
                    </Link>
                </footer>
            </div>
        );
    }

    const s = shareQuery.data;
    const goalsOn = hasAnyGoal(s);

    const formatPair = (deliveryCount: number, totalQuantity: number): string => {
        if (deliveryCount === totalQuantity) {
            return t("stats.flyersOnly", { value: qtyFmt(totalQuantity, lng) });
        }
        return t("stats.addressesAndFlyers", {
            addresses: qtyFmt(deliveryCount, lng),
            flyers: qtyFmt(totalQuantity, lng)
        });
    };

    return (
        <div className="stats-page">
            <div className="stats-page__topbar">
                <header className="stats-page__header">
                    <h1 className="stats-page__title">{t("stats.title")}</h1>
                </header>
                <div className="stats-page__topbar-actions">
                    <WwwPortalNavBar surface="www_stats" />
                </div>
            </div>

            <div className="stats-page__content">
                <main className="stats-page__main">
                <section className="stats-page__hero" aria-labelledby="stats-hero-heading">
                    <h2 id="stats-hero-heading" className="stats-page__hero-kicker">
                        {t("stats.heroKicker")}
                    </h2>
                    <div className="stats-page__hero-main">
                        <p className="stats-page__hero-value" style={{ fontVariantNumeric: "tabular-nums" }}>
                            {qtyFmt(s.lifetimeTotalQuantity, lng)}
                        </p>
                        <p className="stats-page__hero-caption">{t("stats.heroCaption")}</p>
                    </div>
                </section>

                {campaignId && todayCampaignQuery.data ? (
                    <section className="stats-page__card stats-page__card--accent" aria-labelledby="stats-campaign-heading">
                        <h2 id="stats-campaign-heading" className="stats-page__card-title">
                            {t("stats.sectionCampaignToday")}
                        </h2>
                        {campaignName ? <p className="stats-page__campaign-name">{campaignName}</p> : null}
                        <p className="stats-page__campaign-detail">
                            {formatPair(
                                todayCampaignQuery.data.deliveryCount,
                                todayCampaignQuery.data.totalQuantity
                            )}
                        </p>
                    </section>
                ) : null}

                <section className="stats-page__card" aria-labelledby="stats-week-heading">
                    <h2 id="stats-week-heading" className="stats-page__card-title">
                        {t("stats.sectionSevenDays")}
                    </h2>
                    <WeekBars
                        days={[...s.lastSevenDays].reverse()}
                        lng={lng}
                        ariaLabel={t("stats.weekChartLabel")}
                    />
                </section>

                <section className="stats-page__grid" aria-label={t("stats.sectionPeriods")}>
                    <div className="stats-page__tile">
                        <span className="stats-page__tile-label">{t("stats.sectionToday")}</span>
                        <span className="stats-page__tile-value">{qtyFmt(s.todayTotalQuantity, lng)}</span>
                        <span className="stats-page__tile-hint">{t("stats.flyers")}</span>
                    </div>
                    <div className="stats-page__tile">
                        <span className="stats-page__tile-label">{t("stats.tileWeek")}</span>
                        <span className="stats-page__tile-value">{qtyFmt(s.calendarWeekTotalQuantity, lng)}</span>
                        <span className="stats-page__tile-hint">{t("stats.flyersThisCalendarWeek")}</span>
                    </div>
                    <div className="stats-page__tile">
                        <span className="stats-page__tile-label">{t("stats.tileMonth")}</span>
                        <span className="stats-page__tile-value">{qtyFmt(s.calendarMonthTotalQuantity, lng)}</span>
                        <span className="stats-page__tile-hint">{t("stats.flyers")}</span>
                    </div>
                    <div className="stats-page__tile">
                        <span className="stats-page__tile-label">{t("stats.tileYear")}</span>
                        <span className="stats-page__tile-value">{qtyFmt(s.calendarYearTotalQuantity, lng)}</span>
                        <span className="stats-page__tile-hint">{t("stats.flyers")}</span>
                    </div>
                </section>

                {goalsOn ? (
                    <section className="stats-page__card stats-page__card--goals" aria-labelledby="stats-goals-heading">
                        <h2 id="stats-goals-heading" className="stats-page__card-title">
                            {t("stats.sectionGoals")}
                        </h2>
                        <div className="stats-page__goals">
                            {electionDayPace ? (
                                <GoalRow
                                    label={t("stats.goalTodayPace")}
                                    current={s.todayTotalQuantity}
                                    goal={electionDayPace.dailyTarget}
                                    variant="day"
                                    lng={lng}
                                />
                            ) : null}
                            {s.weekGoalQuantity != null ? (
                                <GoalRow
                                    label={t("stats.goalWeek")}
                                    current={s.calendarWeekTotalQuantity}
                                    goal={s.weekGoalQuantity}
                                    variant="week"
                                    lng={lng}
                                />
                            ) : null}
                            {s.monthGoalQuantity != null ? (
                                <GoalRow
                                    label={t("stats.goalMonth")}
                                    current={s.calendarMonthTotalQuantity}
                                    goal={s.monthGoalQuantity}
                                    variant="month"
                                    lng={lng}
                                />
                            ) : null}
                            {s.yearGoalQuantity != null ? (
                                <GoalRow
                                    label={t("stats.goalElectionDay")}
                                    current={s.calendarYearTotalQuantity}
                                    goal={s.yearGoalQuantity}
                                    variant="year"
                                    lng={lng}
                                />
                            ) : null}
                        </div>
                    </section>
                ) : (
                    <section className="stats-page__card stats-page__card--goals stats-page__card--soft">
                        <p className="stats-page__muted stats-page__no-goals">{t("stats.noGoals")}</p>
                    </section>
                )}

                <p className="stats-page__updated">
                    {t("stats.updated")} {updatedLabel}
                </p>
                </main>
            </div>

            <footer className="stats-page__footer">
                <Link to="/" className="stats-page__back">
                    ← {t("stats.backToMap")}
                </Link>
            </footer>
        </div>
    );
};
